import {
  windDeg,
  hasShelteredWaves,
  createShelteredWaveMessage,
} from './swell-helpers';
import { createSwellMap } from './swell-map';

import swellPreview from 'components/swell-preview';
import Tooltip from 'components/tooltip/tooltip';
import SwellIcon from 'components/swell-icon/swell-icon';
import Units from 'utils/units';
import energyIcon from 'components/forecast-table/static/energy.svg';

import './swell-tooltip.scss';

export default class SwellTooltip extends Tooltip {
  renderTooltip() {
    const swellStates = JSON.parse(this.el.dataset.swellState);
    const swellEnergies = JSON.parse(this.el.dataset.swellEnergies);

    const data = swellStates.map((item, index) => {
      if (item !== null) {
        return {
          ...item,
          energy: swellEnergies[index],
        };
      }
      return item;
    });

    const showSwellMap = this.el.dataset.showSwellMap === 'true';

    this.tooltip = document.createElement('div');
    this.tooltip.classList.add('tooltip-popup', 'not_in_print');

    const tooltipHeader = document.createElement('div');
    tooltipHeader.classList.add('tooltip-popup__header');
    this.tooltip.appendChild(tooltipHeader);
    tooltipHeader.innerText = this.el.dataset.date;

    const content = document.createElement('div');
    content.className = 'swell-tooltip__content';

    const flexContainer = document.createElement('div');
    flexContainer.className = 'swell-tooltip__flex';

    const leftSide = document.createElement('div');
    leftSide.className = 'swell-tooltip__left';
    leftSide.style.cssText = 'flex: 0 0 auto;';

    if (showSwellMap) {
      const rating = this.el.dataset.rating;
      const windState = JSON.parse(this.el.dataset.windState);
      const mapContainer = createSwellMap(data, rating, windState);
      leftSide.appendChild(mapContainer);
    }

    const rightSide = document.createElement('div');
    rightSide.className = 'swell-tooltip__right';
    rightSide.style.cssText = 'flex: 1; min-width: 0;';

    const swell = document.createElement('div');
    rightSide.appendChild(swell);

    const wind = JSON.parse(this.el.dataset.wind);
    wind.offshoreness = JSON.parse(this.el.dataset.windState);
    this.unmountSwellPreview = swellPreview(swell, data, wind);

    const table = document.createElement('table');
    const classname = 'swell-tooltip__table';
    table.className = classname;
    rightSide.appendChild(table);

    flexContainer.appendChild(leftSide);
    flexContainer.appendChild(rightSide);

    const headerRow = document.createElement('tr');
    const swellHeader = document.createElement('th');
    swellHeader.innerText = 'Swell';
    headerRow.appendChild(swellHeader);

    const heightHeader = document.createElement('th');
    heightHeader.innerText = 'Height';
    headerRow.appendChild(heightHeader);

    const directionHeader = document.createElement('th');
    directionHeader.innerText = 'Direction';
    headerRow.appendChild(directionHeader);

    const periodHeader = document.createElement('th');
    periodHeader.innerText = 'Period';
    headerRow.appendChild(periodHeader);

    const energyHeader = document.createElement('th');
    const energyHeaderIcon = document.createElement('img');
    energyHeaderIcon.src = energyIcon;
    energyHeaderIcon.alt = 'Energy';
    energyHeaderIcon.className = `${classname}__energy-icon`;
    energyHeader.appendChild(energyHeaderIcon);

    const energyHeaderText = document.createTextNode('kJ');
    energyHeader.appendChild(energyHeaderText);
    headerRow.appendChild(energyHeader);

    table.appendChild(headerRow);

    data.forEach((d, i) => {
      const dataRow = document.createElement('tr');

      const swellLabelCell = document.createElement('th');
      swellLabelCell.innerText = `${i + 1}`;
      dataRow.appendChild(swellLabelCell);

      const heightCell = document.createElement('td');
      if (d) {
        const swellIcon = new SwellIcon({ units: Units });
        swellIcon.update(d);
        heightCell.appendChild(swellIcon.el);
      } else {
        heightCell.innerText = '—';
      }
      dataRow.appendChild(heightCell);

      const directionCell = document.createElement('td');
      if (d && d.letters) {
        directionCell.innerText = d.letters;
        if (d.angle) {
          const angleSpan = document.createElement('span');
          angleSpan.className = `${classname}__angle-cell`;

          let transformedAngle;
          if (d.angle >= 180) {
            transformedAngle = windDeg(d.angle);
          } else {
            transformedAngle = windDeg(d.angle);
          }

          angleSpan.innerText = ` ${transformedAngle}°`;
          directionCell.appendChild(angleSpan);
        }
      } else {
        directionCell.innerText = '—';
      }
      dataRow.appendChild(directionCell);

      const periodCell = document.createElement('td');
      periodCell.className = `${classname}__period-cell`;
      periodCell.innerText = d && d.period ? `${d.period}s` : '—';
      dataRow.appendChild(periodCell);

      const energyCell = document.createElement('td');
      energyCell.className = `${classname}__energy-cell`;

      if (
        d &&
        swellEnergies[i]?.value !== null &&
        swellEnergies[i]?.value !== undefined
      ) {
        energyCell.innerText = swellEnergies[i].value;
        if (swellEnergies[i].value >= 1000) {
          energyCell.style.backgroundColor = swellEnergies[i].colors.background;
          energyCell.style.color = swellEnergies[i].colors.text;
        } else if (swellEnergies[i].value < 100) {
          energyCell.style.color = 'gray';
        } else {
          energyCell.style.color = 'black';
        }
      } else {
        energyCell.innerText = '—';
      }
      dataRow.appendChild(energyCell);

      table.appendChild(dataRow);
    });

    const sumRow = document.createElement('tr');
    sumRow.className = `${classname}__sum-row`;

    const sumLabelCell = document.createElement('th');

    const maxEnergyText = document.createTextNode('Combined Energy');
    sumLabelCell.colSpan = 3;
    sumLabelCell.appendChild(maxEnergyText);
    sumRow.appendChild(sumLabelCell);

    const blankCell2 = document.createElement('td');
    blankCell2.classList.add(`${classname}__blank-cell`);
    blankCell2.innerText = '';
    sumRow.appendChild(blankCell2);

    const energySumLabelCell = document.createElement('td');
    energySumLabelCell.innerText = swellEnergies[4].value;
    energySumLabelCell.style.backgroundColor =
      swellEnergies[4].colors.background;
    energySumLabelCell.style.color = swellEnergies[4].colors.text;
    energySumLabelCell.style.fontWeight = 'bold';

    const energyMaxIcon = document.createElement('img');
    energyMaxIcon.src = energyIcon;
    energyMaxIcon.alt = 'Max Energy';
    energyMaxIcon.className = `${classname}__energy-icon ${classname}__energy-icon--white`;
    energySumLabelCell.prepend(energyMaxIcon);
    sumRow.appendChild(energySumLabelCell);

    table.appendChild(sumRow);

    content.appendChild(flexContainer);

    const shelteredWavesExist = hasShelteredWaves(data);

    if (shelteredWavesExist) {
      const shelteredWave = data.find(wave => wave && wave.height < 0);

      const redMessage = createShelteredWaveMessage(
        'Open sea waves outside the swell window',
        showSwellMap,
        shelteredWave
      );

      content.appendChild(redMessage);
    }

    const tooltipBody = document.createElement('div');
    tooltipBody.classList.add('tooltip-popup__body');
    tooltipBody.classList.add('tooltip-popup__body--custom');
    tooltipBody.appendChild(content);
    this.tooltip.appendChild(tooltipBody);
    this.closeButton = document.createElement('div');
    this.closeButton.classList.add('tooltip-popup__close-button');
    this.tooltip.appendChild(this.closeButton);

    const { currentUnits } = window.unitsConv;
    if (currentUnits !== 'm') {
      window.unitsConv.switchSpans(currentUnits, this.tooltip);
    }
  }

  hide() {
    this.unmountSwellPreview();
    super.hide();
  }
}
